import { OtherExpensesModel } from 'api-hooks/other-expense';
import { SaleModel } from 'api-hooks/sale/common';
import { useGetOtherExpenses } from 'api-hooks/sale/tip';
import { useGetTourOtherExpenses } from 'api-hooks/tour/tour-list/tip';
import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import { ClockSVG, CloseSVG, EditSVG, TrashSVG } from 'common/assets';
import stylesColors from 'common/styles/styles.colors';
import { string2money } from 'common/utils/string';
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeaderContainer,
  DrawerLeftContainer,
  DrawerRightContainer,
} from 'components/common/drawer/styles';
import notification from 'components/common/notification';
import { TableComponent } from 'components/elements';
import Button from 'components/elements/buttons/button';
import { Pagination } from 'components/elements/pagination-component';
import { IColumn } from 'components/elements/table-component/table-component';
import Text from 'components/elements/text';
import { isDataDraft } from 'hooks/use-approval-action';
import useDate from 'hooks/use-date';
import useDrawer from 'hooks/use-drawer';
import useVoidAction, { isDataActive } from 'hooks/use-void-action';
import SalePillStatus from 'legacy-modules/sales/component/status';
import { Action, FooterDivider } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import OtherExpenseCreate from './create';
import OtherExpenseView from './view';

interface OtherExpenseTourListProps {
  type: 'Tour';
  data: TourModel;
  onClose: () => void;
}

interface OtherExpenseSaleListProps {
  type: 'Sale';
  data: SaleModel;
  onClose: () => void;
}

export function useGetOtherExpenseTableList(): IColumn<OtherExpensesModel>[] {
  const { t } = useTranslation();
  const drawer = useDrawer();
  const formatDate = useDate();

  const { onVoid } = useVoidAction('other-expenses');

  const onOpenViewForm = React.useCallback(
    (otherExpense: OtherExpensesModel) => () => {
      drawer.showCustom({
        render: (onClose) => {
          return (
            <OtherExpenseView otherExpense={otherExpense} onClose={onClose} />
          );
        },
      });
    },
    [drawer],
  );

  const onClickVoid = React.useCallback(
    (otherExpense: OtherExpensesModel) => () => {
      onVoid(otherExpense.id);
    },
    [onVoid],
  );

  return [
    {
      header: t('common:invoice_no'),
      accessorKey: 'number',
      minSize: 150,
    },
    {
      header: t('common:created_at'),
      accessorKey: 'createdAt',
      minSize: 128,
      cell: ({ row }) => {
        return formatDate(row.original.createdAt, 'dd MMM yyyy');
      },
    },
    {
      header: t('common:status'),
      accessorKey: 'status',
      minSize: 128,
      cell: ({ row }) => {
        return <SalePillStatus value={row.original.status} />;
      },
    },
    {
      header: t('common:amount'),
      accessorKey: 'amount',
      minSize: 128,
      cell: ({ row }) => {
        return `Rp ${string2money(row.original.amount)}`;
      },
    },
    {
      header: t('common:account'),
      accessorKey: 'accountable.name',
      minSize: 150,
    },
    {
      header: t('common:description'),
      accessorKey: 'description',
      minSize: 500,
    },
    {
      header: '',
      accessorKey: 'action1',
      minSize: 50,
      maxSize: 50,
      cell: ({ row }) => {
        const status = row.original.status;
        const isActive = isDataActive(status);
        if (isActive) {
          return (
            <Action
              onClick={onOpenViewForm(row.original)}
              style={{ width: 32, height: 32 }}
            >
              <EditSVG size={24} />
            </Action>
          );
        }
        return <></>;
      },
    },
    {
      header: '',
      accessorKey: 'action2',
      minSize: 50,
      maxSize: 50,
      cell: ({ row }) => {
        const status = row.original.status;
        const isActive = isDataActive(status);
        const isDraft = isDataDraft(status);
        if (isActive) {
          return (
            <Action
              onClick={onClickVoid(row.original)}
              style={{ width: 32, height: 32 }}
            >
              <TrashSVG
                width={16}
                height={16}
                color={stylesColors.redDefault}
              />
            </Action>
          );
        } else if (isDraft) {
          return (
            <Action onClick={onOpenViewForm(row.original)}>
              <ClockSVG width={16} height={16} />
            </Action>
          );
        }
        return <></>;
      },
    },
  ];
}

export default function OtherExpenseList(
  props: OtherExpenseSaleListProps | OtherExpenseTourListProps,
) {
  const { data, type, onClose } = props;
  const [page, setPage] = React.useState(1);
  const { t } = useTranslation();
  const id = data.id;

  const queryFn =
    type === 'Sale' ? useGetOtherExpenses : useGetTourOtherExpenses;

  const {
    data: otherExpenseData,
    error,
    isFetching,
  } = queryFn({
    id,
    params: { page },
  });

  React.useEffect(() => {
    if (!isFetching && !!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error, isFetching]);

  const otherExpenses = !error ? otherExpenseData?.data || [] : [];
  const lastPage = otherExpenseData?.meta?.lastPage || 0;

  const drawer = useDrawer();
  const onOpenCreateForm = React.useCallback(() => {
    drawer.showCustom({
      render: (onClose) => {
        return (
          <OtherExpenseCreate
            expensableRefType={type}
            data={data}
            onClose={onClose}
          />
        );
      },
    });
  }, [data, drawer, type]);
  const isActive = isDataActive(data.status);
  const isCompleted = type === 'Sale' ? data.isCompleted : !isActive;

  const columns = useGetOtherExpenseTableList();

  return (
    <>
      <DrawerHeaderContainer>
        <DrawerLeftContainer>
          <Text variant="title3Bold">{t('common:other_expenses')}</Text>
        </DrawerLeftContainer>
        <DrawerRightContainer>
          {!isCompleted && (
            <Button
              buttonType="tertiary"
              onClick={onOpenCreateForm}
              disabled={isFetching}
              style={{ padding: '4px 8px' }}
            >
              {t('common:add_extra', { extra: t('common:other_expenses') })}
            </Button>
          )}
          <Action onClick={onClose}>
            <CloseSVG />
          </Action>
        </DrawerRightContainer>
      </DrawerHeaderContainer>
      <DrawerBody>
        <TableComponent
          headerColor={stylesColors.bg1}
          data={otherExpenses}
          columns={columns}
          isLoading={isFetching}
        />
        <FooterDivider />
        <DrawerFooter $style={{ justifyContent: 'center' }}>
          <Pagination
            currentPage={page}
            onPageChange={setPage}
            numPages={lastPage}
          />
        </DrawerFooter>
      </DrawerBody>
    </>
  );
}
